// Variables an admin can insert into a prompt, keyed by the prompt's Braintrust
// slug. Shared by the Default Prompts page and the company round-prompt editor.

export interface PromptVariable {
  name: string;
  value: string;
}

// Interview round prompts, and the fallback for any slug not listed below.
export const roundVariables: PromptVariable[] = [
  { name: 'Company Name', value: '{{company_name}}' },
  { name: 'Project Title', value: '{{project_title}}' },
  { name: 'Round Name', value: '{{round_name}}' },
  { name: 'Candidate Details', value: '{{candidate}}' },
  { name: 'Candidate Name', value: '{{candidate_name}}' },
  { name: 'Questions', value: '{{questions}}' },
  { name: 'Job Description', value: '{{job_description}}' },
  { name: 'Candidate CV', value: '{{candidate_cv}}' },
  { name: 'Previous Round Chat', value: '{{previous_round_chat}}' },
  { name: 'Assessment Criteria', value: '{{assessment_criteria}}' },
];

const endMeeting: PromptVariable[] = [
  { name: 'Project Title', value: '{{project_title}}' },
  { name: 'Round Name', value: '{{round_name}}' },
  { name: 'Company Name', value: '{{company_name}}' },
  { name: 'Candidate Name', value: '{{candidate_name}}' },
  { name: 'Job Description', value: '{{job_description}}' },
  { name: 'Candidate CV', value: '{{candidate_cv}}' },
  { name: 'Previous Round Chat', value: '{{previous_round_chat}}' },
  { name: 'Assessment Criteria', value: '{{assessment_criteria}}' },
];

const applyForJob: PromptVariable[] = [
  { name: 'Candidate Details', value: '{{candidate}}' },
  { name: 'Project Title', value: '{{project_title}}' },
  { name: 'Job Description', value: '{{description}}' },
  { name: 'Must have skills', value: '{{must_have_skills}}' },
  { name: 'Good to have skills', value: '{{good_to_have_skills}}' },
  { name: 'Qualifications', value: '{{qualifications}}' },
  { name: 'Location', value: '{{location}}' },
  { name: 'Assessment Criteria', value: '{{assessment_criteria}}' },
];

const roundScore: PromptVariable[] = [
  { name: 'Project title', value: '{{project_title}}' },
  { name: 'Assessment Criteria', value: '{{assessment_criteria}}' },
  { name: 'Candidate CV', value: '{{candidate_cv}}' },
  { name: 'Job Description', value: '{{job_description}}' },
  { name: 'Current Round Chat', value: '{{current_round_chat}}' },
  { name: 'Previous Round Chat', value: '{{previous_round_chat}}' },
];

const managerPrompt: PromptVariable[] = [
  { name: 'Candidates Details', value: '{{candidates}}' },
];

// Used by both "assessment criteria" and "create round".
const projectAndDescription: PromptVariable[] = [
  { name: 'Project title', value: '{{project_title}}' },
  { name: 'Job Description', value: '{{job_description}}' },
];

const createProject: PromptVariable[] = [
  { name: 'Chat', value: '{{chat}}' },
];

const joblaCreateProject: PromptVariable[] = [
  { name: 'Company Name', value: '{{company_name}}' },
];

// X and LinkedIn post prompts: post settings plus the learning-loop grounding
// (what was said on an episode, what the ICP asks, what works, brand voice).
const socialPost: PromptVariable[] = [
  { name: 'How Many Post You Want To Create', value: '{{count}}' },
  { name: 'Post Language', value: '{{language}}' },
  { name: 'ICP Details', value: '{{campaign}}' },
  { name: 'Campaign Facts', value: '{{facts}}' },
  { name: 'Value Proposition', value: '{{value_proposition}}' },
  { name: 'Podcast Transcript', value: '{{transcript}}' },
  { name: 'Audience Questions', value: '{{questions}}' },
  { name: 'What We Learned Works', value: '{{playbook}}' },
  { name: 'Brand Voice (Manifesto)', value: '{{manifesto}}' },
  { name: 'Hook Types', value: '{{hook_types}}' },
];

// Question bank: rates each question's difficulty (1-10) so the vodcast form
// can list them easy to hard.
const questionDifficulty: PromptVariable[] = [
  { name: 'Questions (id | text, one per line)', value: '{{questions}}' },
  { name: 'Question Count', value: '{{count}}' },
];

// Vodcast form → Generate Questions, ordered easy to hard.
const generateVodcastQuestions: PromptVariable[] = [
  { name: 'Campaign / ICP Facts', value: '{{icp}}' },
  { name: 'Brand Voice (Manifesto)', value: '{{manifesto}}' },
  { name: 'Selected Topics (one per line)', value: '{{topics}}' },
  { name: 'Language', value: '{{language}}' },
];

const variablesBySlug: Record<string, PromptVariable[]> = {
  'apply-for-job-e3bd': applyForJob,
  'end-meeting-edae': endMeeting,
  'round_score': roundScore,
  'manager_prompt': managerPrompt,
  'assessment-criteria-87a5': projectAndDescription,
  'jobla-create-project-3980': projectAndDescription,
  'create-project-039f': createProject,
  'jobla-create-project-1e65': joblaCreateProject,
  'x-post-prompt': socialPost,
  'linkedin-post-prompt': socialPost,
  'question-difficulty-prompt': questionDifficulty,
  'generate-vodcast-questions': generateVodcastQuestions,
};

import { LOOP_PROMPT_BY_SLUG } from './loopPromptMeta';

export const getPromptVariables = (slug?: string): PromptVariable[] =>
  (slug && LOOP_PROMPT_BY_SLUG[slug]?.variables) || (slug && variablesBySlug[slug]) || roundVariables;
